<script setup lang="ts">
  import {
    BottomSheet,
    Button,
    ButtonAppearance,
    ButtonSize,
    Icon,
    IconSize,
    Typo,
    TypoVariant,
  } from '@travelata-libs/ui-kit';
  import { computed, ref } from 'vue';
  import ConfirmationTypeIcon from './ConfirmationTypeIcon.vue';
  import { CONFIRMATION_TYPE_DETAILS, CONFIRMATION_TYPE_SHEET_TEXTS, CONFIRMATION_TYPE_TITLES } from '../config/texts';
  import type { ConfirmationDetail, ConfirmationType } from '../model/types';

  interface ConfirmationTypeSheetProps {
    type: ConfirmationType;
  }

  const props = defineProps<ConfirmationTypeSheetProps>();

  interface ConfirmationTypeSheetEmits {
    (e: 'close'): void;
  }

  const emit = defineEmits<ConfirmationTypeSheetEmits>();

  const title = computed<string>(() => CONFIRMATION_TYPE_TITLES[props.type]);
  const details = computed<readonly ConfirmationDetail[]>(() => CONFIRMATION_TYPE_DETAILS[props.type]);

  const bottomSheetRef = ref<null | { close: () => Promise<void> }>(null);

  // Закрываем через сам BottomSheet, а не голым emit, чтобы шторка уехала с анимацией.
  const onClose = async (): Promise<void> => {
    await bottomSheetRef.value?.close();
  };
</script>

<template>
  <div
    class="confirmation-type-sheet"
    data-testid="confirmation-type-sheet"
  >
    <BottomSheet
      ref="bottomSheetRef"
      root-class="confirmation-type-sheet__sheet"
      :animated="true"
      :icon-closable="true"
      @close="emit('close')"
    >
      <div class="confirmation-type-sheet__content">
        <div class="confirmation-type-sheet__heading">
          <ConfirmationTypeIcon
            :type="type"
            :size="IconSize.XL"
          />
          <Typo
            class="confirmation-type-sheet__title"
            :variant="TypoVariant.Heading5"
            tag="span"
            data-testid="confirmation-type-sheet-title"
          >
            {{ title }}
          </Typo>
        </div>

        <ul
          class="confirmation-type-sheet__details"
          data-testid="confirmation-type-sheet-details"
        >
          <li
            v-for="detail in details"
            :key="detail.text"
            class="confirmation-type-sheet__detail"
          >
            <span class="confirmation-type-sheet__detail-icon">
              <Icon
                :name="detail.icon"
                :size="IconSize.M"
              />
            </span>
            <Typo
              class="confirmation-type-sheet__detail-text"
              :variant="TypoVariant.BodyMRegular"
              tag="span"
            >
              {{ detail.text }}
            </Typo>
          </li>
        </ul>
      </div>

      <template #footer>
        <Button
          class="confirmation-type-sheet__close"
          :appearance="ButtonAppearance.Tertiary"
          :size="ButtonSize.L"
          data-testid="confirmation-type-sheet-close"
          @click="onClose"
        >
          <Typo
            :variant="TypoVariant.BodyLRegular"
            tag="span"
          >
            {{ CONFIRMATION_TYPE_SHEET_TEXTS.close }}
          </Typo>
        </Button>
      </template>
    </BottomSheet>
  </div>
</template>

<style scoped lang="scss">
  .confirmation-type-sheet {
    // Сам BottomSheet телепортируется в body, а на месте остаётся только скрытая обёртка. Без display: contents
    // она была бы flex-элементом карточки и на каждое открытие шторки добавляла ей лишний gap.
    display: contents;

    // В макете над заголовком стоит шапка с одной лишь «таблеткой» свайпа (12 + 4 + 20 = 36px),
    // а BottomSheet без слота header её не рендерит и оставляет свои 28px. Добираем разницу.
    &__content {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-m);
      padding-top: var(--spacing-xs);
    }

    &__heading {
      align-items: center;
      display: flex;
      gap: var(--spacing-xs);
    }

    &__title {
      color: var(--color-text-primary);
    }

    &__details {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-s);
      list-style: none;
      margin: 0;
      padding: 0;
    }

    &__detail {
      align-items: center;
      display: flex;
      gap: var(--spacing-xs);
    }

    // В макете иконка пункта стоит по центру колонки шире её самой, поэтому обёртка:
    // Icon задаёт свой размер инлайновым стилем и шире 20px не растянется.
    &__detail-icon {
      // color — для контурных иконок (stroke="currentColor"), fill — для заливочных: без обоих спрайт
      // берёт серый цвет от .tl-bottom-sheet__content либо чёрный по умолчанию.
      color: var(--color-text-primary);
      display: inline-flex;
      fill: var(--color-text-primary);
      flex-shrink: 0;
      justify-content: center;
      width: 32px;
    }

    &__detail-text {
      color: var(--color-text-primary);
    }

    &__close {
      width: 100%;
    }
  }
</style>
